import { Table } from "antd";
import { cloneDeep, get, isEmpty } from "lodash";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { setLoading } from "../../../../../redux/ducks/Loading";
import Resource from "../../../../../services/api/Resource";
import { paginationDefaults } from "../../../../../utils/properties";
import { formatNumber, showError } from "../../../../../utils/utilities";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

export default function ServicesHours(props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const params = useParams();
  const filters = useSelector((state) => state.FiltersDuck.filters);
  const currentDate = dayjs().format("YYYY");
  const currentLanguage = useSelector((state) => get(state.languageDuck, "currentLanguage", "GB"));

  const [state, setState] = useState({
    expandedTableContent: [],
    expandedOrder: {},
    expandedRowKeys: [],
    expandedRow: null,
    disableNewButton: false,
    pagination: {
      currentPage: paginationDefaults.page,
      pageSize: paginationDefaults.size,
      totalItems: paginationDefaults.total,
    },
    loading: false,
    year: filters.obj.year ? filters.obj.year : currentDate,
  });

  const getExpandedRowData = async (expanded, record = state.expandedOrder) => {
    let rowData = [];
    const keys = [];
    if (expanded) {
      dispatch(setLoading(true));
      const tableData = await Resource.servicesHourListDetails(
        params.id,
        record.projectTaskId,
        filters.obj.year ? filters.obj.year : currentDate
      );
      if (!tableData.error) {
        keys.push(record.key);
        tableData.data.forEach((client, i) => {
          client.key = i;
        });
        rowData = cloneDeep(tableData);
      } else showError(get(tableData, "error.messageCode", "ER000"));
      if (!isEmpty(rowData.data)) {
        rowData.data.forEach((table, idx) => {
          table.key = idx;
        });
      }
    }
    setState((prevState) => ({
      ...prevState,
      expandedRowKeys: keys,
      expandedTableContent: rowData.data,
    }));
    dispatch(setLoading(false));
  };

  const expandedRowRender = () => {
    const expandedColumns = [
      {
        title: <b>{t("projectsManagement.team.tables.services.supplier")}</b>,
        dataIndex: "supplierName",
        width: 200,
        fixed: "left",
      },
      {
        title: <b>{t("projectsManagement.team.tables.services.oda")}</b>,
        dataIndex: "purchaseOrderId",
        width: 100,
      },
      {
        title: <b>{t("projectsManagement.team.tables.services.cdc")}</b>,
        dataIndex: "costCentreId",
        width: 100,
      },
      {
        title: <b>{t("projectsManagement.team.tables.services.rate")}</b>,
        dataIndex: "basePrice",
        width: 100,
        className: "table-value-right",
        render: (amount) => formatNumber({ value: amount, suffix: "EUR", lang: currentLanguage, decimalScale: 0 }),
      },
      {
        title: <b>{t("projectsManagement.team.tables.services.approvedQty")}</b>,
        dataIndex: "quantityApproved",
        fixed: "right",
        width: 100,
        className: "table-value-right",
        render: (amount) => formatNumber({ value: amount, suffix: "HOUR", lang: currentLanguage, decimalScale: 0 }),
      },
      {
        title: <b>{t("projectsManagement.team.tables.services.approvedCosts")}</b>,
        dataIndex: "amountApproved",
        fixed: "right",
        width: 100,
        className: "table-value-right",
        render: (amount) => formatNumber({ value: amount, suffix: "EUR", lang: currentLanguage, decimalScale: 0 }),
      },
    ];
    return (
      <Table
        columns={expandedColumns}
        dataSource={state.expandedTableContent}
        pagination={false}
        size="small"
        bordered
      />
    );
  };

  const columns = [
    {
      title: <b className="space-around">{t("projectsManagement.team.tables.services.expand.enabled")}</b>,
      dataIndex: "enabled",
      width: 100,
      fixed: "left",
      render: (value) =>
        value ? (
          <CheckOutlined className="icon-small space-around noCursorPointer" />
        ) : (
          <CloseOutlined className="icon-small space-around noCursorPointer" />
        ),
    },
    {
      title: <b>{t("projectsManagement.team.tables.services.expand.planningActivity")}</b>,
      dataIndex: "description",
      key: "projectTaskId",
    },
    {
      title: <b>{t("projectsManagement.team.tables.services.approvedQty")}</b>,
      dataIndex: "quantity",
      width: 200,
      render: (amount) => formatNumber({ value: amount, suffix: "HOUR", lang: currentLanguage, decimalScale: 0 }),
    },
    {
      title: <b>{t("projectsManagement.team.tables.services.approvedCosts")}</b>,
      dataIndex: "amount",
      fixed: "right",
      width: 200,
      className: "table-value-right",
      render: (amount) =>
        amount ? formatNumber({ value: amount, suffix: "EUR", lang: currentLanguage, decimalScale: 0 }) : "-",
    },
  ];

  return (
    <>
      <Table
        pagination={{
          current: state.pagination.currentPage,
          pageSize: state.pagination.pageSize,
          total: state.pagination.totalItems,
          onChange: (page, pageSize) => {
            setState((prevState) => ({
              ...prevState,
              pagination: {
                currentPage: page,
                pageSize: pageSize,
                totalItems: state.pagination.totalItems,
              },
            }));
          },
        }}
        columns={columns}
        expandable={{ expandedRowRender }}
        expandedRowKeys={state.expandedRowKeys}
        onExpand={(expanded, record) => getExpandedRowData(expanded, record)}
        dataSource={props.data}
        size="small"
        bordered
      />
    </>
  );
}
